import type { PushSendRequest, PushTemplate } from '../../models/dtos/MarketingPushDto';
import { pushScheduleInstant } from '../../utils/marketingPushSchedule';

export type PushValidationMode='test'|'send'|'preview';
export type PushFieldErrors=Record<string,string>;
export interface PushValidationContext {
    request:PushSendRequest; template?:PushTemplate; mode:PushValidationMode; testIds:string;
    matchReady:boolean; scheduled:boolean; scheduledTime:string; hasPreview:boolean;
}
export function parsePushTestIds(value:string):number[] {
    return value.split(/[\s,;]+/).filter(Boolean).map(Number);
}
export function validatePushForm({request:r,template,mode,testIds,matchReady,scheduled,scheduledTime,hasPreview}:PushValidationContext):PushFieldErrors {
    const errors:PushFieldErrors={};
    if(mode==='send'&&!r.name.trim())errors.name='Enter a sending name.';
    if(r.name.length>160)errors.name='Sending name must be at most 160 characters.';
    if(mode==='send'&&scheduled&&!pushScheduleInstant(scheduledTime))errors.scheduledAt='Choose a valid future sending time.';
    if(!template)errors.templateKey='Choose a notification type. If the list is still loading, try again shortly.';
    else {
        if(template.disabledReason)errors.templateKey=template.disabledReason;
        const platforms=r.platform==='all'?['android','ios']:[r.platform];
        if(!['all','android','ios'].includes(r.platform)||!platforms.every(p=>template.platforms.includes(p)))errors.platform='Choose a platform supported by this notification type.';
        for(const field of template.fields) {
            const key=`parameters.${field.key}`;
            const value=r.parameters[field.key]?.trim()??'';
            if(field.key==='matchId'&&!matchReady)errors[key]='Choose a match from search and wait for successful verification.';
            else if(!value)errors[key]=`${field.label} is required.`;
            else if(field.options?.length&&!field.options.some(o=>o.value===value))errors[key]=`Choose a valid ${field.label.toLowerCase()}.`;
            else if(value.length>2000)errors[key]=`${field.label} must be at most 2,000 characters.`;
            else if(field.numeric&&(!/^\d+$/.test(value)||!Number.isSafeInteger(Number(value))||Number(value)<(field.key.endsWith('Score')?0:1)))errors[key]=`${field.label} must be a ${field.key.endsWith('Score')?'non-negative':'positive'} whole number.`;
            else if(field.key.endsWith('Url')||field.key==='url') {
                try {const url=new URL(value);if(!['http:','https:'].includes(url.protocol)||!url.hostname)throw new Error();}
                catch {errors[key]=`${field.label} must be a valid HTTP or HTTPS URL.`;}
            }
        }
    }
    if(mode!=='test'&&!r.userTags.length)errors.userTags='Select at least one audience tag.';
    const integerRange=(value:number|null|undefined,min:number,max:number,key:string,label:string)=>{
        if(value!=null&&(!Number.isInteger(value)||value<min||value>max))errors[key]=`${label} must be a whole number from ${min} to ${max.toLocaleString()}.`;
    };
    if(mode!=='test')integerRange(r.activeWithinDays,1,3650,'activeWithinDays','Active within days');
    integerRange(r.maxUsers,1,1_000_000,'maxUsers','Maximum users');
    integerRange(r.skipRecentDays,1,3650,'skipRecentDays','Suppression days');
    const variant=template?.variants?.find(v=>v.key===r.parameters.contentVariant);
    const texts={...(variant?.texts??template?.texts??{}),...r.texts};
    if(!texts.en?.title.trim())errors['texts.en.title']='English push title is required for fallback.';
    if(!texts.en?.body.trim())errors['texts.en.body']='English push body is required for fallback.';
    const argumentCount=['homeTeam','awayTeam','homeTeamScore','awayTeamScore'].filter(k=>r.parameters[k]!=null).length;
    for(const [locale,text] of Object.entries(texts)) {
        for(const field of ['title','body','name','desc'] as const) {
            const key=`texts.${locale}.${field}`;
            const value=text[field]??'';
            if(value.length>1000)errors[key]=`${locale.toUpperCase()} ${field} must be at most 1,000 characters.`;
            else if((value.match(/%[sd]/g)?.length??0)>argumentCount)errors[key]=`${locale.toUpperCase()} ${field} has more placeholders than this type's parameters provide.`;
            else if(/%[0-9$]*[a-zA-Z]/.test(value.replace(/%[sd]/g,'')))errors[key]=`${locale.toUpperCase()} ${field} contains an unsupported placeholder.`;
        }
        if(template?.type==='info'&&r.platform!=='android'&&text.desc?.trim()&&(text.title?.trim()||text.body?.trim()))
            errors[`texts.${locale}.desc`]=`Leave the ${locale.toUpperCase()} description empty so the information dialog can appear on iOS.`;
    }
    if(mode==='test') {
        const ids=parsePushTestIds(testIds);
        if(!ids.length||ids.length>20||ids.some(id=>!Number.isSafeInteger(id)||id<=0))errors.testIds='Select 1–20 test users from the search results.';
    }
    if(mode==='send'&&!hasPreview)errors.preview='Click Validate and preview audience before sending. Validate again after changing the draft.';
    return errors;
}

/** Map identifiable server failures; unrecognized errors remain a form-level toast. */
export function pushServerFieldErrors(message:string,template?:PushTemplate):PushFieldErrors {
    if(/^name is required|sending name is too long/i.test(message))return {name:message};
    if(/unknown notification type|unknown template/i.test(message))return {templateKey:message};
    if(/invalid user limit/i.test(message))return {maxUsers:message};
    if(/active within days|invalid activity days/i.test(message))return {activeWithinDays:message};
    if(/invalid suppression days/i.test(message))return {skipRecentDays:message};
    if(/scheduled time/i.test(message))return {scheduledAt:message};
    if(/test users?|test user IDs|explicit recipients/i.test(message))return {testIds:message};
    if(/match (was not found|is flagged|verification|home team|away team|metadata)|different match|choose a valid match|this match is flagged/i.test(message))return {'parameters.matchId':message};
    if(/tag no longer exists|at least one.*tag/i.test(message))return {userTags:message};
    if(/does not support|choose all, android or ios/i.test(message))return {platform:message};
    for(const field of template?.fields??[])if(message.includes(field.label))return {[`parameters.${field.key}`]:message};
    return {};
}
